import { mergeDeep } from 'remeda'
import { ZodString, z } from 'zod'

import { Logger } from '../logger/Logger.js'
import { defaultRetryOptions, fetchRetry, RetryOptions } from './fetchRetry.js'

export class HttpClient {
  private readonly logger: Logger
  private readonly fetchWithRetries: typeof fetch
  constructor(retryOptions: Partial<RetryOptions> = {}, logger = Logger.SILENT) {
    this.logger = logger.for(this)
    const options = mergeDeep(defaultRetryOptions, retryOptions) as RetryOptions
    this.fetchWithRetries = fetchRetry(options)
  }

  async post<T extends z.ZodTypeAny>(
    url: string,
    body: object,
    schema: T,
    headers?: Record<string, string>,
  ): Promise<z.infer<T>> {
    const result = await this.rawPost(url, body, headers)

    if (isZodString(schema)) {
      return await result.text()
    }
    return schema.parse(await result.json())
  }

  async get<T extends z.ZodTypeAny>(url: string, schema: T, headers?: Record<string, string>): Promise<z.infer<T>> {
    const result = await this.rawGet(url, headers)

    if (isZodString(schema)) {
      return schema.parse(await result.text())
    }
    return schema.parse(await result.json())
  }

  async delete<T extends z.ZodTypeAny>(url: string, schema: T, headers?: Record<string, string>): Promise<z.infer<T>> {
    const result = await this.rawDelete(url, headers)

    if (isZodString(schema)) {
      return schema.parse(await result.text())
    }
    return schema.parse(await result.json())
  }

  async rawGet(url: string, headers?: Record<string, string>): Promise<Response> {
    this.logger.trace(`[HttpClient] GET request - ${url}`, { url })

    return await this.fetchWithRetries(url, {
      method: 'GET',
      headers,
    })
  }

  async rawPost(url: string, body: object, headers?: Record<string, string>): Promise<Response> {
    this.logger.trace(`[HttpClient] POST request - ${url}`, { url, body })

    return await this.fetchWithRetries(url, {
      method: 'POST',
      headers: { ...applicationJsonHeader, ...headers },
      body: JSON.stringify(body),
    })
  }

  async rawDelete(url: string, headers?: Record<string, string>): Promise<Response> {
    this.logger.trace(`[HttpClient] DELETE request - ${url}`, { url })

    return await this.fetchWithRetries(url, {
      method: 'DELETE',
      headers,
    })
  }
}

const applicationJsonHeader = {
  'Content-Type': 'application/json',
}

function isZodString(schema: z.ZodTypeAny): schema is ZodString {
  return schema instanceof ZodString || schema._def.typeName === 'ZodString'
}
